// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from 'react';
import formatPrice from '../../services/utils/formatPrice';
import api from '../../services/api';
import { store } from '../../store';
import { negotiation } from '../../store/analytic/actions';

interface NegotiationProps {
  negotiationOption: GQL.IOption;
  contract: GQL.IDebt;
  databaseId: number;
  dealInfos: GQL.IDealInput;
  history: any;
  paymentType?: number;
  creditCardData?: {
    cardNumber: string;
    cardName: string;
    installments: string;
    cardExpiration: string;
    cardCVV: string;
  };
}

export async function HandlerNextAction(props: NegotiationProps) {
  const { contract, negotiationOption, dealInfos, databaseId, history } = props;

  let confirmPaymentError = false;

  try {
    if (props.paymentType === 2) {
      await validateCreditCard(
        negotiationOption,
        props.creditCardData,
        databaseId
      );
    }

    //1 - Set the deal
    const res = ((await setDealState(
      dealInfos,
      databaseId,
      contract.dealId as number
    )) as unknown) as GQL.IState;

    //2 - Get the deal infos
    const dealState = await getDealState({
      dealId: res.id!,
      contract: contract.contracts,
      databaseId: databaseId,
    });

    //3 - Set the negotiation
    await setNegotiation(
      contract,
      negotiationOption,
      dealState[0].id || 0,
      dealState[0].creditorId
    );

    if (props.paymentType === 2) {
      confirmPaymentError = true;

      await confirmCreditCardPayment(
        negotiationOption,
        res,
        props.creditCardData,
        databaseId
      );
    }

    if (dealState.length === 0) return;

    //4 - Go next page
    goNextPage(
      contract,
      negotiationOption,
      dealState[0],
      history,
      res,
      props.paymentType as number
    );
  } catch (error) {
    if (!confirmPaymentError)
      throw new Error(`Error to handle next action: ${error}`);
  }
}

const confirmCreditCardPayment = async (
  negotiationOption: GQL.IOption,
  res: GQL.IState,
  cardData: any,
  databaseId: number
) => {
  const expirationSplitted = cardData?.cardExpiration.split('/');

  try {
    await api.confirmCreditCardPayment({
      databaseId,
      options: {
        operator: 0,
        cardData: {
          expirationMonth: expirationSplitted?.[0] as string,
          expirationYear: expirationSplitted?.[1] as string,
          installmentPlan: negotiationOption?.plan as number,
          name: cardData?.cardName as string,
          number: cardData?.cardNumber as string,
          securityCode: cardData?.cardCVV as string,
        },
        agreementId: res.id as number,
        installmentNumber: 1,
      },
    });
  } catch (error) {
    try {
      await api.deleteDeal({
        agreementId: res.id as number,
        databaseId,
        motivo: 'Falha no pagamento',
      });
      throw new Error(`deal canceled: ${error}`);
    } catch (error) {
      throw new Error(`to cancel deal: ${error}`);
    }
  }
};

const validateCreditCard = async (
  negotiationOption: GQL.IOption,
  cardData: any,
  databaseId: number
) => {
  const expirationSplitted = cardData?.cardExpiration.split('/');

  try {
    await api.validateCreditCard({
      databaseId,
      options: {
        operator: 0,
        cardData: {
          expirationMonth: expirationSplitted?.[0] as string,
          expirationYear: expirationSplitted?.[1] as string,
          installmentPlan: negotiationOption?.plan as number,
          name: cardData?.cardName as string,
          number: cardData?.cardNumber as string,
          securityCode: cardData?.cardCVV as string,
        },
      },
    });
  } catch (error) {
    throw new Error(`to validate card: ${error}`);
  }
};

const setDealState = async (
  props: GQL.IDealInput,
  databaseId: number,
  dealId: number
) => {
  const payload = {
    databaseId: databaseId,
    dealId,
    options: {
      contrato: props.contrato,
      opcaoID: `${props.opcaoID}`,
      valorEntrada: props.valorEntrada,
      valorDemais: props?.valorDemais,
      codigoEmail: props?.codigoEmail || null,
      codigoEndereco: props?.codigoEndereco || null,
      codigoTelefone: props?.codigoTelefone || null,
      auxiliar: props?.auxiliar || null,
      autorizaChequeEspecial: props?.autorizaChequeEspecial || null,
      autorizaDebitoAutomatico: props?.autorizaDebitoAutomatico || null,
    },
  };
  try {
    const res = await api.setDeal(payload);
    return res;
  } catch (error) {
    throw new Error(`to set deal: ${error}`);
  }
};

const getDealState = async (data: GQL.IGetDealStateOnQueryArguments) => {
  try {
    const dealState = await api.getDealState(data);
    return dealState;
  } catch (error) {
    throw new Error(
      `Erro ao pegar as informações do acordo: ${data.dealId} | Mensagem de erro: ${error}`
    );
  }
};

const setNegotiation = async (
  contract: GQL.IDebt,
  negotiationOption: GQL.IOption,
  dealId: number,
  creditorId?: string | null
) => {
  store.dispatch(
    negotiation.request({
      creditor: contract.creditorName,
      dealValue:
        (negotiationOption.firstPortionValue ?? 0) +
        (negotiationOption.plan ? negotiationOption.plan - 1 : 1 - 1) *
          (negotiationOption.othersPortionsValue ?? 0),
      dealType: contract.proposalType || 6,
      delay: contract.longerDelay || 0,
      plan: negotiationOption.plan || 0,
      databaseId: contract?.databaseId || 0,
      dealId: dealId,
      creditorId: creditorId,
    })
  );
};

const goNextPage = (
  contract: GQL.IDebt,
  negotiationOption: GQL.IOption,
  dealState: GQL.IState,
  history: any,
  res: GQL.IState,
  paymentType: number
) => {
  if (dealState.statusDescription === 'Aprovado') {
    const detail = {
      portion:
        negotiationOption &&
        negotiationOption.plan &&
        negotiationOption.plan > 1
          ? `${negotiationOption.plan! - 1} parcelas de R$ ${formatPrice(
              negotiationOption.othersPortionsValue || 0
            )}`
          : '',
      value: formatPrice(negotiationOption.firstPortionValue || 0),
      date: negotiationOption.formatedDueDate,
    };

    if (paymentType >= 0) {
      history.push({
        pathname: '/acordo-finalizado',
        state: {
          contractDebt: contract,
          proposal: negotiationOption,
          dealId: dealState.id!,
          dealState: res,
          detail,
        },
      });
    } else {
      history.push({
        pathname: '/opcoes',
        state: {
          contractDebt: contract,
          proposal: negotiationOption,
          dealId: dealState.id!,
          type: 'negociacao',
          detail,
          previousRoute: '/resumo-negociacao',
        },
      });
    }
  } else {
    history.push({
      pathname: '/negociacao-status',
      state: {
        from: 'negotiation',
        contract: contract,
        negotiation: negotiationOption,
        status: dealState.statusDescription || '',
      },
    });
  }
};
